







































































































import { computed, defineComponent, ref } from "@vue/composition-api";
import { debounce } from "lodash";
import { Vue } from "vue/types/vue";
import { Location } from "vue-router";
import { CallOptions } from "@scrile/api-provider/dist/api/CallOptionsProvider";
import { MessageThread } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { Appointment } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentProvider";
import { AppointmentTypeKind } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentTypeProvider";
import { Message } from "@scrile/api-provider/dist/projects/scrileConnect/MessagesProvider";
import { checkAuthorities, checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useKeepCustomScrollPosition from "@/hooks/useKeepCustomScrollPosition";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { getProfileUrl } from "@/lib/urlHelpers";
import { isWebRTCSupported, isGetUserMediaSupported } from "@/lib/webrtcHelpers";
import config from "@/config";
import { BaseMenuItem, BlockingStatus } from "@/types";
import { EVENTS } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseIcon from "@/components/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BaseList from "@/components/BaseList";
import { BaseListItem } from "@/components/BaseList";
import BaseMenu from "@/components/BaseMenu";
import BasePage from "@/components/BasePage";
import BlockUserController from "@/components/BlockUserController";
import CustomScroll from "@/components/CustomScroll";
import MessageSendWithAttachment from "@/components/MessageSendWithAttachment";
import PageTitle from "@/components/PageTitle";
import UserBookAppointmentsModal from "@/components/UserBookAppointmentModal";
import MessageDeleteConfirm from "@/views/PageMessages/components/MessageDeleteConfirm.vue";
import SubscriberIcon from "@/views/PageMessageThreads/components/SubscriberIcon.vue";
import GroupModal from "./GroupModal.vue";
import JoinAppointmentTooltip from "./JoinAppointmentTooltip";
import MessageList from "./MessageList.vue";
import MessageSystemNote from "./MessageSystemNote.vue";
import SubscriptionOffer from "./SubscriptionOffer";

export default defineComponent({
  name: "MessagesView",
  components: {
    SubscriptionOffer,
    UserBookAppointmentsModal,
    JoinAppointmentTooltip,
    MessageDeleteConfirm,
    MessageList,
    BasePage,
    PageTitle,
    BaseAvatar,
    MessageSendWithAttachment,
    BaseIcon,
    BlockUserController,
    BaseIconButton,
    BaseList,
    BaseListItem,
    BaseMenu,
    MessageSystemNote,
    GroupModal,
    CustomScroll,
    SubscriberIcon,
  },
  props: {
    loading: { type: Boolean, required: true },
    allowResponse: { type: Boolean, required: true },
    authUserId: { type: String, required: true },
    messageThread: { type: Object as () => MessageThread | null },
    messages: { type: Array as () => Message[], required: true },
    minUnreadMessageId: { type: String, required: true },
    opponentMinUnreadMessageId: { type: String, required: true },
    enableScroll: { type: Boolean, default: false },
    user: { type: Object as () => UserPublicData },
    userCallOptions: { type: Object as () => CallOptions | null },
    paymentPackages: { type: Array as () => PaymentPackage[] | null },
    isBookingAvailable: { type: Boolean, required: true },
    isAppointmentStart: { type: Boolean, required: false },
    forceFreeMessages: { type: Boolean, default: false },
    nearestAppointment: { type: Object as () => Appointment | null },
  },
  setup(props) {
    const { authUser, authorities, isCreator } = useAuthUser();
    const showBookAppointmentsModal = ref(false);
    const list = ref<Vue | undefined>();
    const { scrollNode, scrollToBottom } = useKeepCustomScrollPosition(list, { keepScroll: true, behavior: "smooth" });
    const isWebRTCSupport = computed(() => {
      return isWebRTCSupported() && isGetUserMediaSupported();
    });
    const handleCall = debounce(() => {
      if (isWebRTCSupport.value) {
        emitter.emit(EVENTS.CALL_OUTGOING, {
          subject: props.isAppointmentStart ? props.nearestAppointment : null,
          opponentUser: props.user,
        });
      } else {
        emitter.emit(EVENTS.NOTIFICATION_SHOW, { text: $t("This browser does not support WebRTC") });
      }
    }, 1000);
    return {
      authUser,
      authorities,
      list,
      scrollNode,
      AppointmentTypeKind,
      isCreator,
      showBookAppointmentsModal,
      handleCall,
      scrollToBottom,
    };
  },
  data() {
    return {
      blockingStatus: {
        authUser: false,
        opponent: false,
      } as BlockingStatus,
      blockingConfirm: false,
      showGroupModal: false,
      messageToDelete: null as Message | null,
    };
  },
  computed: {
    screenName(): string {
      return this.user?.screenName ?? "";
    },
    pinned(): boolean {
      return !!this.messageThread?.pinTime;
    },
    hidden(): boolean {
      return this.messageThread?.hidden ?? false;
    },
    titleComponent(): string {
      return this.user ? "router-link" : "div";
    },
    profileTo(): Location | undefined {
      return this.user ? getProfileUrl(this.user) : undefined;
    },
    isCallable(): boolean | null | undefined {
      return config.featureCalling && this.user && this.user.id !== this.authUserId && this.userCallOptions?.enabled;
    },
    userId(): string {
      return this.user?.id ?? "";
    },
    isMessageSendShow(): boolean | null | undefined {
      return this.user && !this.isChatBlocked && this.allowResponse;
    },
    threadId(): string | undefined {
      return this.messageThread ? this.messageThread.id : undefined;
    },
    isUserAdmin(): boolean {
      return checkRole(this.user?.role || "", "admin");
    },
    allowManageLists(): boolean {
      return checkAuthorities(this.authorities, ["manageMyGroups"]);
    },
    menuItems(): BaseMenuItem[] {
      const menuItems = [];
      if (this.messageThread && !this.isChatBlocked) {
        menuItems.push({
          name: "pin-user",
          text: !this.pinned ? $t("Mark as important") : $t("Unmark as important"),
          prependIcon: "sci-star",
          ...(this.pinned
            ? {
                prependIconColor: "var(--palette-primary)",
                class: "__active",
              }
            : {}),
        });
      }
      if (this.isCreator) {
        if (this.allowManageLists) {
          menuItems.push({
            name: "add-to-list",
            class: "messages__list-icon __add-to-group",
            text: $t("Add to list"),
            prependIcon: "sci-plus",
          });
        }
        if (!!this.messageThread) {
          menuItems.push({
            name: "toggle-hide",
            class: "messages__list-icon __archive-chat",
            text: !this.hidden ? $t("Archive chat") : $t("Restore chat"),
            prependIcon: !this.hidden ? "sci-archive-thread-outline" : "sci-restore-thread-outline",
          });
        }
        if (!this.isUserAdmin) {
          menuItems.push({
            name: "block-user",
            text: $t(this.blockingStatus.opponent ? $t("Unblock user") : $t("Block user")),
            prependIcon: "sci-blocked",
            prependIconColor: "var(--palette-negative)",
          });
        }
      }

      return menuItems;
    },
    noteText(): string {
      return this.blockingStatus.opponent
        ? $t("You blocked {0}.", [this.user?.screenName])
        : $t("{0} blocked you. You can’t send messages to this creator anymore.", [this.user?.screenName]);
    },
    isChatBlocked(): boolean {
      return this.blockingStatus.opponent || this.blockingStatus.authUser;
    },
  },
  methods: {
    addMessage(message: Message) {
      this.$emit("addMessage", message);
      this.scrollToBottom();
    },
    receivedMessage() {
      if (!this.scrollNode) return;
      const bottomScrollTopPosition = this.scrollNode.scrollHeight - this.scrollNode.offsetHeight;
      if (
        bottomScrollTopPosition - this.scrollNode.scrollTop > 0 &&
        bottomScrollTopPosition - this.scrollNode.scrollTop < 100
      ) {
        this.scrollToBottom();
      }
    },
    closeDeleteConfirm() {
      this.messageToDelete = null;
    },
    handleDeleted() {
      if (this.messageToDelete) {
        this.$emit("deleted", this.messageToDelete.id);
        this.closeDeleteConfirm();
      }
    },
    toggleShowGroupModal() {
      this.showGroupModal = !this.showGroupModal;
    },
  },
  created() {
    this.$on("receivedMessage", this.receivedMessage);
  },
  beforeDestroy() {
    this.$off("receivedMessage", this.receivedMessage);
  },
});
